import { isAllGiftCards } from './category';

/*
 * The category list of the browse page (/buy/{country}) comes from the
 * category groups of the commerce API's /search, which leave out hidden
 * categories (Health & Spa) even when the country has brands in them. These
 * helpers add them back from the build data. Pure: pass the data in.
 */

const GIFT_CARD_GROUP = 'giftcard';

/** Only what the list needs: the catalogue's legacy titles stay out of the page. */
const toNavCategory = ({ id, name, slug }) => ({ id, name, slug });

const isListed = (categories, category) =>
  categories.some(
    (listed) =>
      (listed.id && listed.id === category.id) ||
      (listed.slug || listed.name) === category.slug ||
      listed.name === category.name
  );

/**
 * The hidden categories that have brands in a country.
 *
 *   hiddenCategoryCountries  data/hidden-category-countries.json: `{ [slug]: [countryId] }`
 *   categories               the catalogue's categories (data/cats.json): `{ id, name, slug }`
 *
 * Returns `[{ id, name, slug }]`.
 */
export const hiddenCategoriesForCountry = ({
  categories = [],
  countryId,
  hiddenCategoryCountries = {},
}) =>
  Object.keys(hiddenCategoryCountries)
    .filter((slug) => hiddenCategoryCountries[slug]?.includes(countryId))
    .map((slug) => categories.find((category) => category.slug === slug))
    .filter(Boolean)
    .map(toNavCategory);

/**
 * `groups` (the `/search` category groups) with `categories` added to the gift
 * card group, each where the API would have put it: after "All gift cards",
 * in alphabetical order of the English name, which is how the API sorts.
 *
 * A category the groups already list (the API returns it after all) is left
 * alone, and the input is never mutated. Without a gift card group one is
 * created in front of the others.
 */
export const withCategories = (groups = [], categories = []) => {
  if (!categories.length) return groups;

  const index = groups.findIndex((group) => group.name === GIFT_CARD_GROUP);
  const listed = index === -1 ? [] : [...groups[index].categories];

  for (const category of categories) {
    if (isListed(listed, category)) continue;

    const position = listed.findIndex(
      (other) =>
        !isAllGiftCards(other.slug || other.name) &&
        category.name.localeCompare(other.name, 'en', { sensitivity: 'base' }) < 0
    );

    listed.splice(position === -1 ? listed.length : position, 0, toNavCategory(category));
  }

  if (index === -1) {
    return [{ name: GIFT_CARD_GROUP, categories: listed }, ...groups];
  }

  return groups.map((group, i) => (i === index ? { ...group, categories: listed } : group));
};
